import {
  Button,
  CoursesIcon,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Row,
  Table,
  useDefaultSorting,
} from '@bp/ui-components';
import { FragmentType } from 'client/gql';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useIsDesktop } from 'hooks/useIsDesktop';
import { useTranslation } from 'react-i18next';
import { BpPermissionChecker } from 'utils/PermissionChecker';
import { UseCoursesTableFragment, useCoursesTable } from '../hooks/useCoursesTable';
import { CourseTableType } from '../types/types';

type CooperationCoursesTableProps = {
  query: FragmentType<typeof UseCoursesTableFragment> | undefined;
  permissions: BpPermissionChecker | null;
  onNavigate: (uuid: string) => void;
  onEditorsEdit: (courseUuid: string) => void;
  onViewersEdit: (courseUuid: string) => void;
};

export const CooperationCoursesTable = ({
  query,
  permissions,
  onNavigate,
  onEditorsEdit,
  onViewersEdit,
}: CooperationCoursesTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const isDesktop = useIsDesktop();

  const { data, columns } = useCoursesTable({
    query,
    columns: ['name', 'organization', 'admins', 'schoolYear', 'nextcloud', 'threema'],
    onNavigate,
  });

  const { sorting, onSortingChange } = useDefaultSorting();

  // for Collaboration Groups only Members are editable
  const canEditMembers = (row: Row<CourseTableType>) =>
    permissions?.canUpdateCourse({
      uuid: row.original.uuid,
      managedBy: row.original.managedBy,
      organization: { uuid: pimAuthClaims.getOrganizationUuid() },
    }) && row.original.organizationUuid !== pimAuthClaims.getOrganizationUuid();

  function getDropdownMenuItems(row: Row<CourseTableType>): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [];

    if (canEditMembers(row)) {
      items.push({
        label: t('courses.editEditors'),
        onClick: () => onEditorsEdit(row.original.uuid),
      });
      items.push({
        label: t('courses.editViewers'),
        onClick: () => onViewersEdit(row.original.uuid),
      });
    }

    return items;
  }

  return (
    <Table<CourseTableType>
      isDesktop={isDesktop}
      data-cy='courseList'
      showActionBar
      isOnWhite={false}
      showBorderRadius
      showShadow
      showSort
      canScroll
      sorting={sorting}
      onSortingChange={onSortingChange}
      actionBarSettings={{
        showExpertFilter: true,
      }}
      emptyStateSettings={{
        icon: <CoursesIcon />,
        padding: 'xl',
      }}
      columns={columns}
      data={data}
      lastColWidth='32px'
      lastCol={(row) => {
        const items = getDropdownMenuItems(row);
        const disabled = items.length === 0;
        return (
          <Dropdown
            disabled={disabled}
            trigger={<Button disabled={disabled} hierarchy='ghost' icon={<DotsVerticalIcon />} />}
          >
            <DropdownMenu data={items} />
          </Dropdown>
        );
      }}
    />
  );
};
